import React, { useState } from 'react';
import { motion } from 'framer-motion';
import api from '../utils/api';
import { toast } from 'sonner';
import { MagnifyingGlass, Package, CheckCircle, Truck, MapPin, House, Handshake } from '@phosphor-icons/react';

const STATUSES = [
  { key: 'order_placed', label: 'Order Placed', icon: Package, desc: 'Your order has been received' },
  { key: 'confirmed', label: 'Confirmed', icon: CheckCircle, desc: 'Order confirmed and being prepared' },
  { key: 'dispatched', label: 'Dispatched', icon: Package, desc: 'Order has left the warehouse' },
  { key: 'in_transit', label: 'In Transit', icon: Truck, desc: 'Order is on its way to you' },
  { key: 'out_for_delivery', label: 'Out for Delivery', icon: MapPin, desc: 'Order is out for delivery in your area' },
  { key: 'delivered', label: 'Delivered', icon: House, desc: 'Order has been delivered' },
  { key: 'fulfilled', label: 'Fulfilled', icon: Handshake, desc: 'Order complete' },
];

const OrderTracking = () => {
  const [orderId, setOrderId] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!orderId.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      const response = await api.get(`/orders/track/${orderId.trim()}`);
      setOrder(response.data);
    } catch (error) {
      setOrder(null);
      toast.error('Order not found. Please check the order ID.');
    } finally {
      setLoading(false);
    }
  };

  const currentStatusIndex = order
    ? STATUSES.findIndex(s => s.key === (order.order_status || 'order_placed'))
    : -1;

  const getStatusTimestamp = (statusKey) => {
    if (!order?.status_history) return null;
    const entry = [...order.status_history].reverse().find(h => h.status === statusKey);
    return entry ? new Date(entry.timestamp).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : null;
  };

  return (
    <div data-testid="order-tracking-page" className="min-h-screen pt-24 pb-16 bg-[#F4F4F4]">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-12">
          <h1 data-testid="tracking-heading" className="text-4xl md:text-5xl font-light tracking-tighter mb-4">
            Track Your Order
          </h1>
          <p className="text-base text-[#7a5e75]">Enter your order ID to see the latest status.</p>
        </motion.div>

        {/* Search */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onSubmit={handleTrack}
          className="flex gap-3 mb-12"
        >
          <input
            type="text"
            data-testid="tracking-input"
            value={orderId}
            onChange={e => setOrderId(e.target.value)}
            placeholder="Enter your Order ID"
            className="flex-1 bg-white border border-[#E5E2DC] px-5 py-4 text-sm focus:outline-none focus:border-[#641b4d] transition-colors"
          />
          <button
            type="submit"
            data-testid="tracking-submit"
            disabled={loading}
            className="btn-primary flex items-center gap-2 disabled:opacity-50"
          >
            <MagnifyingGlass size={20} weight="bold" />
            {loading ? 'Tracking...' : 'Track'}
          </button>
        </motion.form>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-12">
            <div className="loading-spinner" />
          </div>
        )}

        {/* No result */}
        {searched && !loading && !order && (
          <div data-testid="order-not-found" className="text-center py-12">
            <p className="text-[#7a5e75]">No order found with this ID. Please check and try again.</p>
          </div>
        )}

        {/* Order Found - Status Timeline */}
        {order && !loading && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            {/* Order Summary */}
            <div className="bg-white border border-[#E5E2DC] p-6 mb-8">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#7a5e75] mb-1">Order ID</p>
                  <p data-testid="tracking-order-id" className="text-sm font-semibold text-[#641b4d] break-all">{order.id}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-widest text-[#7a5e75] mb-1">Total</p>
                  <p className="text-lg font-semibold text-[#641b4d]">₹{order.total?.toFixed(2)}</p>
                </div>
              </div>
              <div className="border-t border-[#E5E2DC] pt-4">
                <p className="text-xs text-[#7a5e75]">
                  Placed on {new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
                <div className="mt-3 space-y-1">
                  {order.items?.map((item, i) => (
                    <p key={i} className="text-sm text-[#641b4d]">
                      {item.product_name} × {item.quantity}
                      {item.customization?.color && <span className="text-[#7a5e75]"> — {item.customization.color}</span>}
                    </p>
                  ))}
                </div>
              </div>
            </div>

            {/* Status Timeline */}
            <div data-testid="status-timeline" className="bg-white border border-[#E5E2DC] p-6">
              <h2 className="text-lg font-light mb-8 text-[#641b4d]">Order Status</h2>
              <div className="relative">
                {STATUSES.map((status, index) => {
                  const isCompleted = index <= currentStatusIndex;
                  const isCurrent = index === currentStatusIndex;
                  const Icon = status.icon;
                  const timestamp = getStatusTimestamp(status.key);
                  const isLast = index === STATUSES.length - 1;

                  return (
                    <div key={status.key} data-testid={`status-step-${status.key}`} className="flex gap-5 relative">
                      {/* Vertical line */}
                      {!isLast && (
                        <div className="absolute left-5 top-10 w-px h-full" style={{ backgroundColor: index < currentStatusIndex ? '#641b4d' : '#E5E2DC' }} />
                      )}

                      {/* Icon circle */}
                      <div className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center z-10 transition-all ${
                        isCurrent ? 'bg-[#bc64aa] text-white' :
                        isCompleted ? 'bg-[#641b4d] text-white' :
                        'bg-[#F4F4F4] border-2 border-[#E5E2DC] text-[#E5E2DC]'
                      }`}>
                        <Icon size={18} weight={isCompleted ? 'fill' : 'light'} />
                      </div>

                      {/* Content */}
                      <div className={`pb-8 ${isLast ? 'pb-0' : ''}`}>
                        <p className={`text-sm font-semibold ${isCompleted ? 'text-[#641b4d]' : 'text-[#7a5e75]'}`}>
                          {status.label}
                          {isCurrent && <span className="ml-2 text-xs bg-[#bc64aa]/10 text-[#bc64aa] px-2 py-0.5">Current</span>}
                        </p>
                        <p className={`text-xs mt-1 ${isCompleted ? 'text-[#7a5e75]' : 'text-[#E5E2DC]'}`}>
                          {status.desc}
                        </p>
                        {timestamp && (
                          <p className="text-xs text-[#7a5e75] mt-1">{timestamp}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Shipping Info */}
            {order.shipping_info && (
              <div className="bg-white border border-[#E5E2DC] p-6 mt-6">
                <h2 className="text-lg font-light mb-4 text-[#641b4d]">Shipping To</h2>
                <div className="text-sm text-[#7a5e75] space-y-1">
                  <p className="text-[#641b4d] font-semibold">{order.shipping_info.name}</p>
                  <p>{order.shipping_info.address}</p>
                  <p>{order.shipping_info.city}, {order.shipping_info.state} — {order.shipping_info.pincode}</p>
                  <p>{order.shipping_info.phone}</p>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default OrderTracking;
